import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateHallModal from './CreateHallModal';

/**
 * Dropdown shown in the LiveWorld controls bar where the hall name is displayed.
 * Clicking the name opens a panel with the top 5 most populated halls plus a
 * "Create new Hall" action.
 *
 * Navigation: picking a hall navigates to its public URL (/, /@username, /:slug)
 * rather than trying to swap the hall in place — this keeps address-bar state
 * in sync with the active hall and is a natural fit for the /:slug routing.
 */
const HallDropdown = ({
  slug,
  displayName,
  appUser,
  isAuthenticated
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [topHalls, setTopHalls] = useState([]);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const rootRef = useRef(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Fetch top halls when the panel opens
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch('/api/halls/top?limit=5', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        setTopHalls(Array.isArray(data?.halls) ? data.halls : []);
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Failed to load halls');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  const go = (hallSlug) => {
    setOpen(false);
    if (!hallSlug) return;
    const normalized = String(hallSlug).toLowerCase();
    if (normalized === 'commons') {
      navigate('/');
    } else if (hallSlug.startsWith('@')) {
      navigate(`/${hallSlug}`);
    } else {
      navigate(`/${hallSlug}`);
    }
  };

  return (
    <div ref={rootRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 font-semibold whitespace-nowrap hover:text-blue-300 focus:outline-none"
        title="Switch hall"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span>{displayName || slug || 'Commons'}</span>
        <span className="text-[10px] opacity-70">▾</span>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 w-56 bg-gray-900 border border-gray-700 rounded-md shadow-xl z-50 text-sm"
          role="menu"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-800">
            Top Halls
          </div>
          {loading && (
            <div className="px-3 py-2 text-gray-400">Loading…</div>
          )}
          {error && (
            <div className="px-3 py-2 text-red-400">{error}</div>
          )}
          {!loading && !error && topHalls.length === 0 && (
            <div className="px-3 py-2 text-gray-500">No halls yet.</div>
          )}
          {!loading && !error && topHalls.map(h => (
            <button
              key={h.slug}
              type="button"
              onClick={() => go(h.slug)}
              className={`w-full text-left px-3 py-2 hover:bg-gray-800 flex items-center justify-between gap-2 ${
                h.slug.toLowerCase() === (slug || '').toLowerCase() ? 'bg-gray-800' : ''
              }`}
            >
              <span className="truncate">{h.displayName}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">{h.occupants}</span>
            </button>
          ))}

          <div className="border-t border-gray-800 my-1" />

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setShowCreate(true);
            }}
            className="w-full text-left px-3 py-2 hover:bg-gray-800 text-blue-300"
            disabled={!isAuthenticated}
            title={!isAuthenticated ? 'Sign in to create a Hall' : 'Create a new Hall'}
          >
            + Create new Hall
          </button>
        </div>
      )}

      {showCreate && (
        <CreateHallModal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          appUser={appUser}
          onCreated={(hall) => {
            setShowCreate(false);
            if (hall?.slug) go(hall.slug);
          }}
        />
      )}
    </div>
  );
};

export default HallDropdown;
